// src/hooks/useCachedEnvSelection.ts
// -----------------------------------------------------------------------------
// useCachedEnvSelection — per-user environment multi-selection for the
// patient-create form, persisted in the DATABASE (source of truth) with
// localStorage as an optimistic / first-paint cache.
//
// Behavior (cursor_logs/042):
//   - Source of truth is `user.ui_preferences` (DB), passed in as
//     `dbSelectedEnvIds` / `dbSeenEnvIds`. localStorage mirrors it so the UI
//     never flashes empty before the user fetch resolves and keeps working
//     offline. When both exist and disagree, the DB wins.
//   - On first init we reconcile the saved selection against the envs the user
//     currently has access to, then APPEND any genuinely-new env (one that has
//     appeared since last visit) so newly-created environments are checked by
//     default — WITHOUT re-checking an env the user deliberately unchecked.
//     This needs a "seen" baseline: the set of every env the user has been
//     shown. new = available − seen; selection = (saved ∩ available) ∪ new.
//   - FIRST-EVER load (no saved selection anywhere): select only the preferred
//     env if the user is a member, else the first available env. (Joan's
//     decision — NOT all-checked. Also keeps the onboarding tutorial, which
//     relies on exactly the first env being pre-checked, working.)
//   - Legacy migration: a user with an old localStorage selection but no "seen"
//     list keeps their selection and adopts the current available set as the
//     baseline (nothing appended that first load).
//   - On every change we write-through to localStorage AND call `onPersist`
//     (the caller fires the debounced DB PATCH + optimistic Redux update).
//
// Joan, 2026-05-27: original localStorage-only version.
// Joan, 2026-06-10: DB-backed + seen-set append (cursor_logs/042).
// -----------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";
import type { Environment } from "../types";

export interface UseCachedEnvSelectionParams {
  /** User PK / ID — namespaces the cache so two users on the same machine
   *  don't share selections. Pass null/undefined while auth is still loading. */
  userId: string | number | null | undefined;
  /** Envs the user has access to, fetched from the backend. */
  availableEnvironments: Environment[];
  /** Optional preferred default (e.g. consortium env) when nothing is saved.
   *  Honored only if the user is currently a member of this env. */
  preferredEnvId?: string | null;
  /** DB-persisted selection (from user.ui_preferences.patient_create_env_ids).
   *  null/undefined = not loaded / never saved. The DB is the source of truth. */
  dbSelectedEnvIds?: string[] | null;
  /** DB-persisted "seen" baseline
   *  (user.ui_preferences.patient_create_seen_env_ids). Its PRESENCE signals the
   *  user has saved before under the new logic. */
  dbSeenEnvIds?: string[] | null;
  /** Write-through callback: persist the new selection + seen baseline to the
   *  DB. The hook always updates localStorage itself; this adds the DB layer. */
  onPersist?: (next: { selectedEnvIds: string[]; seenEnvIds: string[] }) => void;
}

interface CachedShape {
  selected: string[];
  seen?: string[];
}

// v2 stores an object {selected, seen}; v1 stored a bare string[] (selected
// only). We read v1 as a legacy fallback so existing users migrate cleanly.
function storageKeyV2(userId: string | number): string {
  return `oncocraft.envSelection.v2.${userId}`;
}
function storageKeyV1(userId: string | number): string {
  return `oncocraft.envSelection.v1.${userId}`;
}

// Env ids may be persisted as strings OR numbers (cursor_logs/044: the live
// /environments/ payload serves numbers despite `Environment.id: string`, and
// the suggestion/activation path produces strings). Accept both and keep the
// raw values; ALL matching below is String-coerced and the hook re-emits the
// CANONICAL ids from availableEnvironments, which self-heals mixed-type
// state already persisted in localStorage or ui_preferences.
function _idArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((x: unknown) => typeof x === "string" || typeof x === "number")
    .map((x) => String(x));
}

function readCache(userId: string | number): CachedShape | null {
  try {
    const rawV2 = window.localStorage.getItem(storageKeyV2(userId));
    if (rawV2) {
      const parsed = JSON.parse(rawV2);
      if (parsed && Array.isArray(parsed.selected)) {
        return {
          selected: _idArray(parsed.selected),
          seen: Array.isArray(parsed.seen) ? _idArray(parsed.seen) : undefined,
        };
      }
    }
    // Legacy v1: bare array of selected ids, no "seen".
    const rawV1 = window.localStorage.getItem(storageKeyV1(userId));
    if (rawV1) {
      const parsed = JSON.parse(rawV1);
      if (Array.isArray(parsed)) {
        return { selected: _idArray(parsed), seen: undefined };
      }
    }
  } catch {
    /* corrupted / private mode — treat as no cache */
  }
  return null;
}

function writeCache(userId: string | number, shape: CachedShape): void {
  try {
    window.localStorage.setItem(storageKeyV2(userId), JSON.stringify(shape));
  } catch {
    /* private mode / quota — no-op */
  }
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const bs = new Set(b);
  return a.every((x) => bs.has(x));
}

export function useCachedEnvSelection({
  userId,
  availableEnvironments,
  preferredEnvId,
  dbSelectedEnvIds,
  dbSeenEnvIds,
  onPersist,
}: UseCachedEnvSelectionParams): [string[], (ids: string[]) => void] {
  const [selectedEnvIds, setSelectedEnvIdsState] = useState<string[]>([]);
  // The current "seen" baseline; updated at init, read on every user change so
  // toggling selection never loses it.
  const seenRef = useRef<string[]>([]);
  // Track whether we've already initialized so we don't keep overwriting
  // the user's clicks every time availableEnvironments changes.
  const initializedRef = useRef(false);
  const lastUserIdRef = useRef<string | number | null | undefined>(undefined);
  // Keep the latest onPersist without making setSelectedEnvIds change identity.
  const onPersistRef = useRef(onPersist);
  useEffect(() => {
    onPersistRef.current = onPersist;
  }, [onPersist]);

  // Re-initialize when the user changes (different login on same tab).
  useEffect(() => {
    if (lastUserIdRef.current !== userId) {
      initializedRef.current = false;
      lastUserIdRef.current = userId;
    }
  }, [userId]);

  // Initial pick once we have both a user and envs.
  useEffect(() => {
    if (initializedRef.current) return;
    if (userId == null) return;
    if (availableEnvironments.length === 0) return;

    const available = availableEnvironments.map((e) => e.id);
    // String-keyed lookup → CANONICAL id (cursor_logs/044): saved ids may be
    // strings or numbers; we match by String key and always emit the id value
    // the live availableEnvironments list uses, so downstream strict
    // comparisons (and the persisted prefs) converge on one representation.
    const availableByKey = new Map(
      availableEnvironments.map((e) => [String(e.id), e.id]),
    );

    // Resolve the saved state: DB is authoritative; fall back to localStorage.
    const local = readCache(userId);
    let savedSelected: string[] | undefined;
    let savedSeen: string[] | undefined;
    if (dbSelectedEnvIds != null || dbSeenEnvIds != null) {
      savedSelected = dbSelectedEnvIds ?? [];
      savedSeen = dbSeenEnvIds ?? undefined;
    } else if (local) {
      savedSelected = local.selected;
      savedSeen = local.seen;
    }

    const firstEverFallback = (): string[] => {
      if (preferredEnvId != null && availableByKey.has(String(preferredEnvId))) {
        return [availableByKey.get(String(preferredEnvId)) as string];
      }
      return available.length > 0 ? [available[0]] : [];
    };

    let next: string[];
    let nextSeen: string[];

    if (savedSelected === undefined) {
      // FIRST-EVER load: first env only (or preferred env if a member).
      next = firstEverFallback();
      nextSeen = available;
    } else {
      // Match by String key, emit canonical ids, dedup mixed-type duplicates.
      const selectedKeys = new Set<string>();
      const selected: string[] = [];
      for (const id of savedSelected) {
        const key = String(id);
        if (availableByKey.has(key) && !selectedKeys.has(key)) {
          selectedKeys.add(key);
          selected.push(availableByKey.get(key) as string);
        }
      }
      if (savedSeen === undefined) {
        // Legacy migration: keep selection, adopt current available as the
        // baseline so nothing is treated as "new" on this first reconcile.
        next = selected;
      } else {
        const seenKeys = new Set(savedSeen.map((id) => String(id)));
        const newlyAppeared = available.filter(
          (id) => !seenKeys.has(String(id)) && !selectedKeys.has(String(id)),
        );
        next = [...selected, ...newlyAppeared];
      }
      nextSeen = available;
      if (next.length === 0) {
        // Everything got unchecked or the envs were removed — never leave the
        // form with zero selectable envs.
        next = firstEverFallback();
      }
    }

    seenRef.current = nextSeen;
    setSelectedEnvIdsState(next);
    initializedRef.current = true;

    // Persist only if the reconciled state actually differs from what's saved
    // (avoids a PATCH on every dashboard mount for a steady-state user).
    const changed =
      savedSelected === undefined ||
      !sameSet(next, savedSelected) ||
      savedSeen === undefined ||
      !sameSet(nextSeen, savedSeen);
    if (changed) {
      writeCache(userId, { selected: next, seen: nextSeen });
      onPersistRef.current?.({ selectedEnvIds: next, seenEnvIds: nextSeen });
    }
  }, [
    userId,
    availableEnvironments,
    preferredEnvId,
    dbSelectedEnvIds,
    dbSeenEnvIds,
  ]);

  const setSelectedEnvIds = useCallback(
    (ids: string[]) => {
      setSelectedEnvIdsState(ids);
      if (userId != null) {
        const seen = seenRef.current;
        writeCache(userId, { selected: ids, seen });
        onPersistRef.current?.({ selectedEnvIds: ids, seenEnvIds: seen });
      }
    },
    [userId],
  );

  return [selectedEnvIds, setSelectedEnvIds];
}

export default useCachedEnvSelection;
